import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  // MYB-371: Send PT cookie (.tscorp.ai domain) on all requests.
  // Backend middleware reads this cookie — no Authorization header needed.
  withCredentials: true,
});

// 에러 타입 정의
export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// MYB-371: Redirect dedup flag — prevents multiple simultaneous redirects to login
let isRedirecting = false;

async function retryAfter429(error: any): Promise<any> {
  const config = error.config;
  if (!config) return Promise.reject(error);
  config.__retryCount = config.__retryCount || 0;
  if (config.__retryCount >= 2) return Promise.reject(error);
  config.__retryCount += 1;
  const delay = 1000 * config.__retryCount;
  await new Promise(r => setTimeout(r, delay));
  return api.request(config);
}

// 요청 인터셉터: FormData Content-Type 자동 처리
// MYB-371: Bearer token injection REMOVED — PT cookie is sent automatically via withCredentials
api.interceptors.request.use((config) => {
  // TSCORP-464: X-View-As header REMOVED (security risk — unaudited impersonation)
  // All impersonation is now via Platform Token viewAs claim (auth.tscorp.ai)

  // FormData일 때 Content-Type 삭제 -> axios가 boundary 포함하여 자동 설정
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// 응답 인터셉터: envelope 언래핑 + 에러 처리 (BACK-211)
api.interceptors.response.use(
  (response) => {
    // 표준 envelope 감지: {success: true, data: <원본>} -> data만 추출
    if (
      response.data &&
      typeof response.data === 'object' &&
      response.data.success === true &&
      'data' in response.data
    ) {
      response.data = response.data.data;
    }
    return response;
  },
  async (error) => {
    // 타임아웃 에러
    if (error.code === 'ECONNABORTED') {
      throw new ApiError(
        '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.',
        'TIMEOUT',
        408
      );
    }

    // 네트워크 에러
    if (!error.response) {
      throw new ApiError(
        'API 서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.',
        'NETWORK_ERROR',
        0,
        error.message
      );
    }

    const status = error.response.status;
    const data = error.response.data;

    if (status === 429) {
      return retryAfter429(error);
    }

    // 402: LV2 step-up required — emit global event for StepUpModal
    if (status === 402 && data?.detail?.error === 'stepup_required') {
      window.dispatchEvent(new CustomEvent('tscorp:stepup_required'));
      throw new ApiError(
        'LV2 인증이 필요합니다.',
        'STEPUP_REQUIRED',
        402
      );
    }

    // 401: Platform Token expired or absent — redirect to SSO login
    // MYB-371: No more refresh token logic. PT cookie is managed by auth.tscorp.ai.
    // When PT expires, the user needs to re-authenticate via SSO.
    if (status === 401) {
      if (!isRedirecting) {
        isRedirecting = true;
        // Redirect to landing page — AuthContext will detect missing PT and show login
        window.location.href = '/';
      }
      throw new ApiError('인증이 만료되었습니다. 다시 로그인해주세요.', 'UNAUTHORIZED', 401);
    }

    // 표준 envelope 에러 형식 감지: {success: false, error: {code, message, details}}
    if (data?.success === false && data?.error) {
      const err = data.error;
      throw new ApiError(
        err.message || '알 수 없는 오류가 발생했습니다.',
        err.code || 'UNKNOWN_ERROR',
        status,
        err.details
      );
    }

    // 레거시 에러 형식 fallback
    let message = '알 수 없는 오류가 발생했습니다.';
    let code = 'UNKNOWN_ERROR';

    if (status === 400) {
      message = data?.detail || '잘못된 요청입니다.';
      code = 'BAD_REQUEST';
    } else if (status === 404) {
      message = '요청한 데이터를 찾을 수 없습니다.';
      code = 'NOT_FOUND';
    } else if (status === 500) {
      message = '서버 내부 오류가 발생했습니다. 관리자에게 문의하세요.';
      code = 'SERVER_ERROR';
    } else if (status === 503) {
      message = '서버가 일시적으로 사용 불가능합니다. 잠시 후 다시 시도해주세요.';
      code = 'SERVICE_UNAVAILABLE';
    } else if (data?.detail) {
      message = data.detail;
    }

    throw new ApiError(message, code, status, data);
  }
);
